import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { COUNTRY_CODES } from '../../config/constants'
import { Flag } from '../Icon/Flag'
import { Icon } from '../Icon/Icon'
import styles from './CountryCodeSelect.module.scss'

interface CountryCodeSelectProps {
  onChange: (code: string) => void
  value: string
}

export const CountryCodeSelect = ({ onChange, value }: CountryCodeSelectProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const current = COUNTRY_CODES.find((item) => item.code === value) ?? COUNTRY_CODES[0]

  useEffect(() => {
    if (!open) {
      return
    }
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className={styles.select}>
      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t('promoModal.countryCodeAria')}
        className={styles.trigger}
        type="button"
        onClick={() => {
          setOpen((prev) => !prev)
        }}
      >
        <Flag code={current.flag} size={24} />
        <span>{current.code}</span>
        <Icon
          className={`${styles.chevron}${open ? ` ${styles.open}` : ''}`}
          name="chevron-down"
          size={20}
        />
      </button>
      {open ? (
        <ul className={styles.menu} role="listbox">
          {COUNTRY_CODES.map((item) => (
            <li key={item.code}>
              <button
                aria-selected={item.code === value}
                className={styles.option}
                role="option"
                type="button"
                onClick={() => {
                  onChange(item.code)
                  setOpen(false)
                }}
              >
                <Flag code={item.flag} size={24} />
                <span>{item.code}</span>
                {item.code === value ? (
                  <Icon className={styles.check} name="check" size={20} />
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
